/* ═══════════════════════════════════════════════════════════════════════
   Moonwater — Unified Cart Store
   assets/mw-cart-store.js
   Load EARLY in theme.liquid <head>, before any cart-related snippet:
     <script src="{{ 'mw-cart-store.js' | asset_url }}"></script>
   (No `defer` — we want window.MWCart defined before other scripts run.
    The store does no DOM work on load, so head placement is safe & cheap.)

   ──────────────────────────────────────────────────────────────────────
   WHAT THIS IS
   The single authority for cart state across the entire store. Every
   surface (header count, free-shipping bar, BOGO, cart drawer, quick-view,
   product grids, bundle builder, sampler) stops talking to /cart.js
   directly and instead:
     • calls MWCart.add / .change / .remove / .clear  to WRITE
     • calls MWCart.subscribe(fn)                      to LISTEN
   The store performs every write through XHR (so Seal Subscriptions'
   site-wide window.fetch wrapper can never interfere), serializes all
   writes through ONE queue (so a user removal and a BOGO sync can never
   race into a 400), fetches /cart.js EXACTLY ONCE after a settled change,
   and broadcasts the fresh cart to all subscribers.

   WHY A QUEUE
   The old system had the BOGO pair-remove loop posting change.js while
   syncBogo() independently posted change.js for overlapping line keys.
   The second post to an already-removed key returned 400 Bad Request.
   A serial queue makes that structurally impossible: writes run one at a
   time, in order, each seeing the cart state the previous one left behind.

   WHY NO MutationObserver / NO fetch-wrapping
   Those were the feedback loop. State now flows OUT from the store to
   subscribers; nothing needs to watch the DOM or intercept fetch to know
   the cart changed, because the store is the thing that changed it.
   ═══════════════════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  if (window.MWCart) return; // singleton guard — safe across section reloads

  var listeners = [];   // subscriber callbacks: fn(cart)
  var state     = null; // last known cart object (Shopify /cart.js shape)
  var chain     = Promise.resolve(); // serial write queue

  /* ── Low-level XHR helpers ───────────────────────────────────────────
     Everything goes through XHR, never fetch. Seal wraps window.fetch
     during its own init; XHR is untouched, so cart writes are reliable
     regardless of third-party script state. */

  function xhr(method, url, body) {
    return new Promise(function (resolve, reject) {
      var r = new XMLHttpRequest();
      r.open(method, url, true);
      r.setRequestHeader('Accept', 'application/json');
      if (body != null) r.setRequestHeader('Content-Type', 'application/json');
      r.onload = function () {
        if (r.status >= 200 && r.status < 300) {
          try { resolve(r.responseText ? JSON.parse(r.responseText) : {}); }
          catch (e) { resolve({}); }
        } else {
          // Surface Shopify's error body so callers can react meaningfully
          var err = new Error(method + ' ' + url + ' → ' + r.status);
          err.status = r.status;
          try { err.body = JSON.parse(r.responseText); } catch (e) {}
          reject(err);
        }
      };
      r.onerror = function () { reject(new Error(method + ' ' + url + ' network error')); };
      r.send(body != null ? JSON.stringify(body) : null);
    });
  }

  function getCartRaw() {
    return xhr('GET', '/cart.js', null);
  }

  /* ── Broadcast ───────────────────────────────────────────────────────
     After any settled write we fetch the cart once and hand the SAME
     object to every subscriber. Subscribers must not refetch — they read
     what they're given. A subscriber throwing must not break the others. */

  function broadcast(cart) {
    state = cart;
    for (var i = 0; i < listeners.length; i++) {
      try { listeners[i](cart); }
      catch (e) { console.error('[MWCart] subscriber error', e); }
    }
    // Back-compat bridge: some legacy code still listens for these events.
    // We emit them so nothing breaks during migration; they carry the cart
    // in event.detail so listeners can read state without refetching.
    try {
      document.dispatchEvent(new CustomEvent('mw:cart:updated', { detail: cart }));
    } catch (e) {}
  }

  /* ── The write queue ─────────────────────────────────────────────────
     Each public write appends a task to `chain`. Tasks run strictly in
     order. We coalesce the post-write /cart.js fetch: the task does its
     mutation, then resolves; a single refetch+broadcast happens after the
     task settles. Callers get a promise that resolves with the fresh cart. */

  function enqueue(mutator) {
    var run = chain.then(function () {
      return mutator();              // perform the write(s)
    }).then(function () {
      return getCartRaw();           // read settled state once
    }).then(function (cart) {
      broadcast(cart);               // tell everyone
      return cart;
    });
    // Keep the chain alive even if this task rejected, so one failed write
    // doesn't permanently jam the queue. The returned promise still rejects
    // for the caller.
    chain = run.catch(function () {});
    return run;
  }

  /* ── Public API ──────────────────────────────────────────────────────
     All writes return a promise resolving to the fresh cart object. */

  var MWCart = {
    /* Read the last broadcast cart synchronously (may be null before first
       load). For a guaranteed-fresh read, use refresh(). */
    get: function () { return state; },

    /* Force a refetch + broadcast without mutating. Use sparingly — most
       code should just subscribe and react. */
    refresh: function () {
      var run = chain.then(getCartRaw).then(function (cart) {
        broadcast(cart);
        return cart;
      });
      chain = run.catch(function () {});
      return run;
    },

    add: function (payload) {
      return enqueue(function () { return xhr('POST', '/cart/add.js', payload); });
    },

    /* change by line key OR line number OR variant id — Shopify accepts
       { id: <key|variantId>, quantity } or { line: <n>, quantity }.
       Pass whichever you have; key is safest (stable across reorders). */
    change: function (payload) {
      return enqueue(function () { return xhr('POST', '/cart/change.js', payload); });
    },

    remove: function (keyOrLine) {
      var payload = (typeof keyOrLine === 'number')
        ? { line: keyOrLine, quantity: 0 }
        : { id: keyOrLine, quantity: 0 };
      return enqueue(function () { return xhr('POST', '/cart/change.js', payload); });
    },

    update: function (payload) {
      return enqueue(function () { return xhr('POST', '/cart/update.js', payload); });
    },

    clear: function () {
      return enqueue(function () { return xhr('POST', '/cart/clear.js', {}); });
    },

    /* Apply a SEQUENCE of changes safely, resolving each line KEY to its
       current line NUMBER immediately before the write, re-reading the cart
       between writes. This avoids the change.js-by-key 400 on same-variant
       lines (the duplicate-pack case). `changes` is an array of:
         { key, quantity, properties? }        → change that line
         { addVariant, quantity, properties? } → add a new line
       Adds are by variant id (no key collision). One broadcast at the end. */
    applyChanges: function (changes) {
      if (!changes || !changes.length) return Promise.resolve(state);
      var run = chain.then(function () {
        return changes.reduce(function (c, op) {
          return c.then(function () {
            if (op.addVariant) {
              return xhr('POST', '/cart/add.js', {
                id: Number(op.addVariant),
                quantity: op.quantity,
                properties: op.properties || {}
              });
            }
            return getCartRaw().then(function (cart) {
              var lineNo = -1;
              (cart.items || []).forEach(function (it, idx) {
                if (it.key === op.key && lineNo === -1) lineNo = idx + 1;
              });
              if (lineNo === -1) return; // line already gone; skip
              var payload = { line: lineNo, quantity: op.quantity };
              if (op.properties) payload.properties = op.properties;
              return xhr('POST', '/cart/change.js', payload);
            });
          });
        }, Promise.resolve());
      }).then(getCartRaw).then(function (cart) {
        broadcast(cart);
        return cart;
      });
      chain = run.catch(function () {});
      return run;
    },

    /* Remove several lines reliably. Removing by line KEY can 400 when
       multiple lines share a variant id; removing by line NUMBER is
       robust, but numbers shift as lines are removed — so we resolve keys
       to current line numbers and remove HIGHEST line number first, which
       leaves the lower line numbers valid. We re-read the cart fresh at
       the start so the line numbers match reality. One broadcast at the end.
       `keys` is an array of line keys. */
    removeKeys: function (keys) {
      if (!keys || !keys.length) return Promise.resolve(state);
      var run = chain.then(function () {
        return getCartRaw();
      }).then(function (cart) {
        // Map each target key to its current 1-based line number.
        var lineNos = [];
        (cart.items || []).forEach(function (it, idx) {
          if (keys.indexOf(it.key) > -1) lineNos.push(idx + 1);
        });
        // Remove highest line number first so earlier removals don't
        // renumber the lines we haven't removed yet.
        lineNos.sort(function (a, b) { return b - a; });
        return lineNos.reduce(function (c, lineNo) {
          return c.then(function () {
            return xhr('POST', '/cart/change.js', { line: lineNo, quantity: 0 });
          });
        }, Promise.resolve());
      }).then(getCartRaw).then(function (cart) {
        broadcast(cart);
        return cart;
      });
      chain = run.catch(function () {});
      return run;
    },

    /* Run several writes as ONE atomic batch on the queue, then a single
       refetch+broadcast at the end. Use this for multi-step operations
       (BOGO pair removal, flavor swap = remove+add) so intermediate states
       never broadcast and never race. `steps` is a function returning a
       promise; it receives a `tx` object with the same write verbs, but
       those run immediately in-sequence WITHOUT their own broadcast. */
    batch: function (steps) {
      var run = chain.then(function () {
        var tx = {
          add:    function (p) { return xhr('POST', '/cart/add.js', p); },
          change: function (p) { return xhr('POST', '/cart/change.js', p); },
          remove: function (k) {
            var p = (typeof k === 'number') ? { line: k, quantity: 0 } : { id: k, quantity: 0 };
            return xhr('POST', '/cart/change.js', p);
          },
          update: function (p) { return xhr('POST', '/cart/update.js', p); },
          clear:  function ()  { return xhr('POST', '/cart/clear.js', {}); },
          getCart: getCartRaw
        };
        return steps(tx);
      }).then(getCartRaw).then(function (cart) {
        broadcast(cart);
        return cart;
      });
      chain = run.catch(function () {});
      return run;
    },

    /* Subscribe to cart changes. Returns an unsubscribe function. If the
       store already has state, the new subscriber is called immediately
       with it so late-loading surfaces sync without waiting for a change. */
    subscribe: function (fn) {
      if (typeof fn !== 'function') return function () {};
      listeners.push(fn);
      if (state) { try { fn(state); } catch (e) { console.error('[MWCart] subscriber error', e); } }
      return function () {
        var i = listeners.indexOf(fn);
        if (i > -1) listeners.splice(i, 1);
      };
    },

    /* ── Canonical count rule (absorbed from header.liquid Smart Count) ──
       Bundle = 1 (grouped as a single line for the customer).
       BOGO free pack = 0 (it's a gift; it shouldn't inflate the count).
       Everything else = its quantity.
       This is THE count used everywhere the header bubble / count badges
       are written, so the number is consistent across the whole store. */
    smartCount: function (cart) {
      cart = cart || state;
      if (!cart || !cart.items) return 0;
      var seenBundles = {};
      var count = 0;
      cart.items.forEach(function (item) {
        var props = item.properties || {};
        if (props._bogo_free) return;            // gift → 0
        var bundleId = props._bundle_id;
        if (bundleId) {                          // bundle → 1 per bundle
          if (!seenBundles[bundleId]) { seenBundles[bundleId] = true; count += 1; }
          return;
        }
        count += item.quantity;                  // regular → quantity
      });
      return count;
    }
  };

  window.MWCart = MWCart;

  /* ── Initial load ────────────────────────────────────────────────────
     One fetch on startup so subscribers have state ASAP. We don't touch
     the DOM here — surfaces render themselves from the broadcast. */
  getCartRaw().then(broadcast).catch(function (e) {
    console.warn('[MWCart] initial cart load failed', e);
  });

})();